import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarDays, Loader2 } from "lucide-react";

import FechaInput from "@/components/FechaInput";
const MESES = ["", "Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const fmt = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d} ${MESES[parseInt(m, 10)]} ${y}`;
};

export default function Calendario() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [form, setForm] = useState({ initial_per_number: 1, start_date: "", initial_per_type: "standard", special_duration: 3 });
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasConfig, setHasConfig] = useState(false);

  const load = async (y) => {
    setLoading(true);
    try {
      const [cfgRes, perRes] = await Promise.allSettled([
        api.get(`/calendar/config/${y}`),
        api.get(`/calendar/periods/${y}`),
      ]);
      if (cfgRes.status === "fulfilled") {
        const c = cfgRes.value.data;
        setForm({
          initial_per_number: c.initial_per_number,
          start_date: c.start_date,
          initial_per_type: c.initial_per_type,
          special_duration: c.special_duration || 3,
        });
        setHasConfig(true);
      } else {
        setHasConfig(false);
      }
      setPeriods(perRes.status === "fulfilled" ? perRes.value.data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(year); /* eslint-disable-next-line */ }, [year]);

  const guardar = async () => {
    if (!form.start_date) { toast.error("Captura la fecha de inicio"); return; }
    setSaving(true);
    try {
      const payload = {
        year: Number(year),
        initial_per_number: Number(form.initial_per_number),
        start_date: form.start_date,
        initial_per_type: form.initial_per_type,
        special_duration: Number(form.special_duration) || 3,
      };
      const r = await api.post(`/calendar/config`, payload);
      toast.success(`Calendario generado: ${r.data.periods_generated} periodos para ${year}`);
      await load(year);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const yearOpts = [year - 1, year, year + 1, year + 2].filter((v, i, a) => a.indexOf(v) === i);

  return (
    <div className="p-6 lg:p-8 max-w-5xl" data-testid="calendario-page">
      <div className="flex items-center gap-2.5 mb-1">
        <CalendarDays className="text-blue-600" size={22} />
        <h1 className="text-2xl font-head font-extrabold tracking-tight">Calendario de Periodos</h1>
      </div>
      <p className="text-sm text-slate-500 mb-6">
        Configura el calendario de periodos (PER0X) por año. Los periodos se generan y guardan automáticamente,
        y su etiqueta aparece junto a las fechas del sistema.
      </p>

      <Card className="border-slate-200 shadow-sm p-5 mb-6" data-testid="calendario-config-card">
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
          <div>
            <Label className="text-xs">Año</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="h-9 mt-1" data-testid="cal-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {yearOpts.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">N° del primer PER</Label>
            <Input type="number" className="h-9 mt-1" value={form.initial_per_number} onChange={(e) => setForm({ ...form, initial_per_number: e.target.value })} data-testid="cal-initial-number" />
          </div>
          <div>
            <Label className="text-xs">Fecha de inicio</Label>
            <FechaInput className="h-9 mt-1" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="cal-start-date" />
          </div>
          <div>
            <Label className="text-xs">Tipo primer periodo</Label>
            <Select value={form.initial_per_type} onValueChange={(v) => setForm({ ...form, initial_per_type: v })}>
              <SelectTrigger className="h-9 mt-1" data-testid="cal-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard (jue→mié)</SelectItem>
                <SelectItem value="special">Special</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Duración special (días)</Label>
            <Input type="number" className="h-9 mt-1" value={form.special_duration} disabled={form.initial_per_type !== "special"} onChange={(e) => setForm({ ...form, special_duration: e.target.value })} data-testid="cal-special-duration" />
          </div>
        </div>
        <div className="flex items-center gap-3 mt-5">
          <Button onClick={guardar} disabled={saving} className="bg-blue-600 hover:bg-blue-700 h-9" data-testid="cal-generate-btn">
            {saving ? <><Loader2 size={15} className="animate-spin mr-1.5" /> Generando…</> : (hasConfig ? "Regenerar calendario" : "Generar calendario")}
          </Button>
          {hasConfig && <span className="text-xs text-slate-400">Config guardada · {periods.length} periodos generados</span>}
        </div>
        {form.initial_per_type === "standard" && form.start_date && (
          <p className="text-[11px] text-amber-600 mt-2">Si la fecha de inicio no cae en jueves, el primer periodo estándar se moverá automáticamente al primer jueves posterior.</p>
        )}
      </Card>

      <Card className="border-slate-200 shadow-sm" data-testid="calendario-periodos-card">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-head font-bold tracking-tight">Periodos {year}</h3>
          <span className="text-sm text-slate-500" data-testid="cal-periods-count">{periods.length} periodos</span>
        </div>
        {loading ? (
          <div className="p-8 text-center text-slate-400"><Loader2 className="animate-spin inline mr-2" size={16} /> Cargando…</div>
        ) : periods.length === 0 ? (
          <p className="p-8 text-center text-slate-400">Aún no hay periodos generados para {year}. Captura la configuración y genera el calendario.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/80 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3 font-semibold">Periodo</th>
                  <th className="px-4 py-3 font-semibold">Inicio (jueves)</th>
                  <th className="px-4 py-3 font-semibold">Cierre (miércoles)</th>
                  <th className="px-4 py-3 font-semibold">Mes</th>
                  <th className="px-4 py-3 font-semibold">Bim.</th>
                  <th className="px-4 py-3 font-semibold">Tipo</th>
                </tr>
              </thead>
              <tbody>
                {periods.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`cal-period-${p.number}`}>
                    <td className="px-4 py-2.5 font-semibold text-slate-800">PER{String(p.number).padStart(2, "0")}</td>
                    <td className="px-4 py-2.5 text-slate-600">{fmt(p.start_date)}</td>
                    <td className="px-4 py-2.5 text-slate-600">{fmt(p.end_date)}</td>
                    <td className="px-4 py-2.5 text-slate-600">{MESES[p.month]}</td>
                    <td className="px-4 py-2.5 text-slate-600">B{p.bimester}</td>
                    <td className="px-4 py-2.5">
                      {p.is_special
                        ? <span className="text-[11px] font-semibold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">Special</span>
                        : <span className="text-[11px] text-slate-400">Standard</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
